import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { AiOutlineClose } from "react-icons/ai";
import { BiCheckCircle, BiXCircle } from "react-icons/bi";

const displayFont = { fontFamily: "'Fraunces', serif" };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
});

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-[#A6AFBD] mb-0.5">{label}</p>
    <p className="text-sm text-[#101826]">{value || "—"}</p>
  </div>
);

const DocLink = ({ label, path }) => {
  if (!path) return null;
  const url = `${BACKEND_URL}${path}`;
  const isImage = /\.(jpe?g|png|webp|gif)$/i.test(path);

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex flex-col items-center gap-2 border border-[#EDF1F7] rounded-xl p-3 hover:border-[#2E63B8] transition-colors"
    >
      {isImage ? (
        <img src={url} alt={label} className="w-full h-24 object-cover rounded-lg" />
      ) : (
        <div className="w-full h-24 rounded-lg bg-[#F5F8FC] flex items-center justify-center text-[#5A6478] text-xs">
          View PDF / File
        </div>
      )}
      <span className="text-xs text-[#5A6478] text-center">{label}</span>
    </a>
  );
};

const SchoolReviewModal = ({ schoolId, onClose, onActionComplete }) => {
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/v1/admin/schools/${schoolId}`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (data.success) setSchool(data.school);
        else toast.error(data.message || "Failed to load details");
      } catch (error) {
        toast.error("Failed to load school details");
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [schoolId]);

  const handleApprove = async () => {
    if (!window.confirm(`Approve ${school.name}? Login credentials will be emailed immediately.`)) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/admin/schools/${schoolId}/approve`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      toast.success("School approved and credentials sent");
      onActionComplete();
    } catch (error) {
      toast.error(error.message || "Failed to approve school");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/admin/schools/${schoolId}/reject`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ reason: rejectReason }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      toast.success("Registration rejected, applicant notified");
      onActionComplete();
    } catch (error) {
      toast.error(error.message || "Failed to reject school");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EDF1F7]">
          <h2 style={displayFont} className="text-xl text-[#101826]">
            {loading ? "Loading..." : school?.name}
          </h2>
          <button onClick={onClose} className="text-[#5A6478] hover:text-[#101826]">
            <AiOutlineClose className="text-xl" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 flex-1">
          {loading ? (
            <p className="text-[#A6AFBD] text-sm">Fetching registration details...</p>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-[#163863] mb-3">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="School Name" value={school.name} />
                  <Field label="School ID" value={school.schoolId} />
                  <Field label="Established Year" value={school.establishedYear} />
                  <Field label="Board Affiliation" value={school.boardAffiliation} />
                  <Field label="Medium" value={school.medium} />
                  <Field label="Subscription Type" value={school.subscriptionType} />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[#163863] mb-3">Contact</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Login Email" value={school.email} />
                  <Field label="Contact Email" value={school.contactEmail} />
                  <Field label="Phone" value={school.phone} />
                  <Field label="School URL" value={school.schoolUrl} />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[#163863] mb-3">Address</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Full Address" value={school.fullAddress} />
                  <Field label="Landmark" value={school.landmark} />
                  <Field label="City" value={school.city} />
                  <Field label="State" value={school.state} />
                  <Field label="Pincode" value={school.pincode} />
                  <Field label="Country" value={school.country} />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[#163863] mb-3">Submitted Documents</h3>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  <DocLink label="Logo" path={school.documents?.logo} />
                  <DocLink label="Registration Certificate" path={school.documents?.registrationCert} />
                  <DocLink label="Affiliation Letter" path={school.documents?.affiliationLetter} />
                  <DocLink label="Principal ID" path={school.documents?.principalID} />
                  <DocLink label="PAN Card" path={school.documents?.panCard} />
                  {(school.documents?.banner || []).map((b, i) => (
                    <DocLink key={i} label={`Banner ${i + 1}`} path={b} />
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[#163863] mb-3">Review Status</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Submitted On" value={new Date(school.createdAt).toLocaleString("en-IN")} />
                  <Field label="Current Status" value={school.status} />
                  {school.reviewedBy && <Field label="Reviewed By" value={school.reviewedBy} />}
                  {school.reviewedAt && (
                    <Field label="Reviewed On" value={new Date(school.reviewedAt).toLocaleString("en-IN")} />
                  )}
                  {school.rejectionReason && (
                    <Field label="Rejection Reason" value={school.rejectionReason} />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {!loading && school?.status === "pending" && (
          <div className="border-t border-[#EDF1F7] px-6 py-4">
            {showRejectForm ? (
              <div className="space-y-3">
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  placeholder="Explain why this registration is being rejected (sent to the applicant)..."
                  className="w-full px-4 py-2.5 bg-white border border-[#E3E8F0] rounded-xl text-sm outline-none focus:border-[#C23A3A] focus:shadow-[0_0_0_4px_rgba(194,58,58,0.12)] transition-all"
                />
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setShowRejectForm(false)}
                    className="px-4 py-2 rounded-full text-sm text-[#5A6478] hover:bg-[#F5F8FC]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={actionLoading}
                    className="px-5 py-2 rounded-full bg-[#C23A3A] text-white text-sm font-semibold hover:bg-[#a63030] disabled:opacity-50"
                  >
                    {actionLoading ? "Rejecting..." : "Confirm Rejection"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowRejectForm(true)}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-full border border-[#C23A3A] text-[#C23A3A] text-sm font-semibold hover:bg-[#FCEAEA] transition-colors"
                >
                  <BiXCircle /> Reject
                </button>
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-full bg-gradient-to-r from-[#163863] to-[#2E63B8] text-white text-sm font-semibold shadow-md hover:shadow-lg disabled:opacity-50"
                >
                  <BiCheckCircle /> {actionLoading ? "Approving..." : "Approve & Launch"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolReviewModal;
